import { Component, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DocumentConstants } from 'app/constants/document.constants';
import { AttachmentModel } from 'app/models/attachment.model';
import { FormService } from 'app/services/form.service';
import { UploadFileDialogComponent } from '../../dialogs/upload-file-dialog/upload-file-dialog.component';
import { SubstanceTypeEnum } from 'app/enums/substance-type-enum';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormValidator } from 'app/ui-components/form/form-validators/form-validators';
import { Subscription } from 'rxjs';
import { fieldPatterns } from 'app/utilities/form.utilities';

@Component({
  selector: 'app-molecular-formula-dialog',
  templateUrl: './molecular-formula-dialog.component.html',
  styleUrls: ['./molecular-formula-dialog.component.scss']
})
export class MolecularFormulaDialogComponent implements OnInit {
  @Input({required: true}) substanceType!: SubstanceTypeEnum;
  private allCaseAttachmentsSubscription: Subscription;
  readonly dialog = inject(MatDialog);

  // For readability in the validators
  private FORM_A = 'FormA';
  private FORM_B = 'FormB';
  private FORM_C = 'FormC';
  private FORM_D = 'FormD';
  private FORM_E = 'FormE';
  private FORM_F = 'FormF';

  private MOLECULAR_FORMULA_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_F]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];
  private MOLECULAR_FORMULA_SINGLE_ENTITY_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];
  private MOLECULAR_FORMULA_FOR_SALT_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];

  constructor(protected formService: FormService) {
    this.allCaseAttachmentsSubscription = this.formService.getAllCaseAttachmentsUpdate().subscribe(
        () => {
          this.validatorCheck();
      });
  }

  ngOnInit(): void {
    this.validatorCheck(); 
  }
  
  private validatorCheck() {
    if(this.hasMolecularFormulaFile) {
      this.removeValidators(false);
    } else {
      this.addValidators(false);
    }
  }

  public openDialog(): void {
    const dialogRef = this.dialog.open(UploadFileDialogComponent, {
        data: { fileName: this.attachmentNamePrefix }
      });
    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.removeValidators(true);
      }
    });
  }

  get hasMolecularFormulaFile(): boolean {
    return this.formService.doesCurrentFormHaveAttachmentForSection(this.attachmentNamePrefix);
  }

  get attachments(): AttachmentModel[] {
    return this.formService.allAttachmentsForSection(this.attachmentNamePrefix);
  }

  get isNoneFormulaInputType() {
    return this.substanceType === SubstanceTypeEnum.None;
  }

  get isSingleEntityFormulaInputType() {
    return this.substanceType === SubstanceTypeEnum.SingleEntity;
  }

  get isSaltFormulaInputType() {
    return this.substanceType === SubstanceTypeEnum.Salt;
  }

  get isContactLensMaterialInputType() {
    return this.substanceType === SubstanceTypeEnum.ContactLensMaterial;
  }

  get isBiologicsInputType() {
    return this.substanceType === SubstanceTypeEnum.Biologics;
  }

  get attachmentNamePrefix() {
    if(this.isNoneFormulaInputType || this.isBiologicsInputType) {
      return DocumentConstants.MOLECULAR_FORMULA;
    }

    if(this.isSingleEntityFormulaInputType) {
      return DocumentConstants.MOLECULAR_FORMULA_SINGLE_ENTITY;
    }
    if(this.isSaltFormulaInputType) {
      return DocumentConstants.MOLECULAR_FORMULA_FOR_SALT;
    }

    if(this.isContactLensMaterialInputType) {
      return DocumentConstants.MONOMER_AND_POLYMER_FORMULAS;
    }

    return '';
  }

  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }

  get formFieldName() {
    if(this.isNoneFormulaInputType || this.isBiologicsInputType) {
      return 'molecularFormula';
    }

    if(this.isSingleEntityFormulaInputType) {
      return 'molecularFormulaForSingleEntity'; 
    }
    if(this.isSaltFormulaInputType) {
      return 'molecularFormulaForSaltForm'; 
    }

    if(this.isContactLensMaterialInputType) {
      return 'monomerAndPolymerFormulas';
    }

    return '';
  }

  public addValidators(markControl: boolean) {
    const targetControl = this.currentForm.get(this.formFieldName);

    if (!targetControl) {
        return;
    }
  
    targetControl.setValidators(this.formFieldValidators);

    // Force re-validation if not the initial call
    setTimeout(() => {
      if(markControl) {
        targetControl.markAsTouched();
        targetControl.markAsDirty();
      }
      targetControl.updateValueAndValidity();
      this.formService.invalidFormFields();
    });
  }

  private removeValidators(markControl: boolean) {
    const targetControl = this.currentForm.controls[this.formFieldName];

    if (!targetControl) {
        return;
    }

    targetControl.clearValidators();
    targetControl.setErrors(null);
    
    setTimeout(() => {
      if(markControl) {
        targetControl.markAsTouched();
        targetControl.markAsDirty();
      }
      targetControl.updateValueAndValidity();
      this.formService.invalidFormFields();
    });
  }

  get formFieldValidators(): ValidatorFn[] {
    if(this.isNoneFormulaInputType || this.isBiologicsInputType) {
      return this.MOLECULAR_FORMULA_VALIDATORS;
    }

    if(this.isSingleEntityFormulaInputType) {
      return this.MOLECULAR_FORMULA_SINGLE_ENTITY_VALIDATORS; 
    }
    if(this.isSaltFormulaInputType) {
      return this.MOLECULAR_FORMULA_FOR_SALT_VALIDATORS;
    }
    return [];
  }
}
